/**
 * Lends the brand's faces to apps that render inside a shadow root.
 *
 * Shopify Forms draws its pop-up inside `shopify-forms-embed`, a custom element
 * with its own adopted stylesheets, so the theme's CSS never reaches the text.
 * Its font picker only offers Shopify's library, and the brand faces are not in
 * it: they are self hosted by pn-fonts.liquid.
 *
 * An `@font-face` is registered on the document rather than scoped to a shadow
 * tree, so the file the page already loaded can be used in there. Nothing is
 * downloaded twice; the form is simply told which family to ask for.
 *
 * Headings take Fraunces and everything else Hanken Grotesk, which is the way
 * round the rest of the store works.
 *
 * The same sheet turns the form's minimised teaser into a corner ribbon, at
 * the owner's request (14 Oct 2026), drawn in the brand's own parts: a gold
 * foil triangle in the bottom left corner, the obsidian text of the gold
 * buttons, a hairline engraved along the long edge, and the store's heading
 * pair along the diagonal, a small tracked label over a Fraunces line. The
 * close button is the obsidian of the header with a gold cross, just off the
 * long edge. The Forms editor sets only the teaser's text and colours, so the
 * shape lives here. The app's class names are hashed per release, so the rules
 * hold on its test id and on the close button's label instead.
 *
 * The teaser text comes from the Forms editor and may be any length, so it is
 * split rather than squeezed: the discount ("10% off") becomes the Fraunces
 * line and the rest ("First Order!") the label above it. Text with no discount
 * in it is set whole, smaller and wrapped. The split is written to data
 * attributes and drawn by the heading's pseudo elements, so the app's own nodes
 * are never changed, and the button's aria-label still reads the full text.
 *
 * The teaser button keeps its square box, but only the triangle takes clicks:
 * the box ignores the pointer, and the triangle drawn on it takes it back,
 * clipped to the triangle. The close button sits outside the triangle and takes
 * the pointer itself.
 */
(function () {
  var CSS_RIBBON = [
    '/* The corner ribbon. The wrapper pads the full pop-up away from the window',
    '   edge with an inline style; the ribbon sits flush in the corner. */',
    ':host #app-embed[class*="_appEmbedRootTeaser_"] {',
    '  padding: 0 !important;',
    '}',
    ':host button[data-testid="form-teaser"] {',
    '  --pn-ribbon: 140px;',
    '  position: relative;',
    '  display: block;',
    '  width: var(--pn-ribbon);',
    '  height: var(--pn-ribbon);',
    '  max-width: none;',
    '  margin: 0 !important;',
    '  padding: 0;',
    '  border: 0;',
    '  border-radius: 0;',
    '  background: none;',
    '  box-shadow: none;',
    '  overflow: visible;',
    '  pointer-events: none;',
    '  filter: drop-shadow(0 3px 10px rgb(17 17 15 / 0.12));',
    '  animation: pn-ribbon-in 640ms cubic-bezier(0.22, 1, 0.36, 1) both;',
    '}',
    '/* The foil triangle. */',
    ':host button[data-testid="form-teaser"]::before {',
    "  content: '';",
    '  position: absolute;',
    '  inset: 0;',
    '  background: var(--pn-gold-foil, linear-gradient(24deg, #BE9B4E 0%, #D6B25E 55%, #E6CD8C 100%));',
    '  clip-path: polygon(0 0, 0 100%, 100% 100%);',
    '  pointer-events: auto;',
    '  cursor: pointer;',
    '  transition: filter 200ms cubic-bezier(0.4, 0, 0.2, 1);',
    '}',
    ':host button[data-testid="form-teaser"]:hover::before {',
    '  filter: brightness(1.06);',
    '}',
    '/* The engraved hairline, 10px in from the long edge. */',
    ':host button[data-testid="form-teaser"]::after {',
    "  content: '';",
    '  position: absolute;',
    '  inset: 0;',
    '  background-color: rgb(17 17 15 / 0.3);',
    '  clip-path: polygon(0 10px, 0 11px, calc(100% - 11px) 100%, calc(100% - 10px) 100%);',
    '  pointer-events: none;',
    '}',
    "/* The heading pair, along the diagonal, a little above the triangle's centroid",
    "   where the triangle is wider. The app's own text is hidden; the pair is",
    '   drawn from the split. */',
    ':host button[data-testid="form-teaser"] h3 {',
    '  position: absolute;',
    '  top: calc(66.7% - 1px);',
    '  left: calc(33.3% + 1px);',
    '  z-index: 1;',
    '  display: flex;',
    '  flex-direction: column;',
    '  align-items: center;',
    '  gap: 6px;',
    '  margin: 0;',
    '  font-size: 0;',
    "  /* The app's own text stays in the heading at size 0, but its line height",
    '     would still open a blank line between the pair. */',
    '  line-height: 0 !important;',
    '  color: var(--pn-obsidian, #11110F);',
    '  text-align: center;',
    '  white-space: nowrap;',
    '  transform: translate(-50%, -50%) rotate(45deg);',
    '  pointer-events: none;',
    '}',
    ':host button[data-testid="form-teaser"] h3::before {',
    '  content: attr(data-pn-small);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 9px;',
    '  font-weight: 600;',
    '  line-height: 1;',
    '  letter-spacing: 0.18em;',
    '  text-transform: uppercase;',
    '}',
    ':host button[data-testid="form-teaser"] h3[data-pn-small=""]::before {',
    '  display: none;',
    '}',
    ':host button[data-testid="form-teaser"] h3::after {',
    '  content: attr(data-pn-big);',
    "  font-family: 'Fraunces', Georgia, serif;",
    '  font-size: 18px;',
    '  font-weight: 600;',
    '  line-height: 1;',
    "  font-variation-settings: 'SOFT' 30, 'WONK' 0;",
    '}',
    '/* Text with no discount in it, set whole. */',
    ':host button[data-testid="form-teaser"] h3[data-pn-whole]::after {',
    '  width: 92px;',
    '  font-size: 12px;',
    '  line-height: 1.15;',
    '  white-space: normal;',
    '}',
    ':host button[data-testid="form-teaser"]:focus-visible {',
    '  outline: none;',
    '}',
    ':host button[data-testid="form-teaser"]:focus-visible h3::after {',
    '  text-decoration: underline;',
    '  text-decoration-thickness: 1px;',
    '  text-underline-offset: 4px;',
    '}',
    ':host button[data-testid="form-teaser"] [aria-label="Close modal"] {',
    '  position: absolute;',
    '  bottom: 8px;',
    '  left: calc(100% - 6px);',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  width: 24px;',
    '  height: 24px;',
    '  margin: 0;',
    '  padding: 0;',
    '  border-radius: 50%;',
    '  background-color: var(--pn-obsidian, #11110F);',
    '  color: var(--pn-gold, #D6B25E);',
    '  box-shadow: 0 0 0 1.5px rgb(247 242 232 / 0.55);',
    '  pointer-events: auto;',
    '  cursor: pointer;',
    '  transition: color 200ms cubic-bezier(0.4, 0, 0.2, 1);',
    '}',
    ':host button[data-testid="form-teaser"] [aria-label="Close modal"]:hover {',
    '  color: var(--pn-ivory, #F7F2E8);',
    '}',
    ':host button[data-testid="form-teaser"] [aria-label="Close modal"] svg {',
    '  width: 9px;',
    '  height: 9px;',
    '}',
    ':host button[data-testid="form-teaser"] [aria-label="Close modal"] path {',
    '  fill: currentColor;',
    '}',
    '@media screen and (max-width: 749px) {',
    '  :host button[data-testid="form-teaser"] {',
    '    --pn-ribbon: 100px;',
    '  }',
    '  :host button[data-testid="form-teaser"]::after {',
    '    clip-path: polygon(0 8px, 0 9px, calc(100% - 9px) 100%, calc(100% - 8px) 100%);',
    '  }',
    '  :host button[data-testid="form-teaser"] h3 {',
    '    top: calc(66.7% - 2px);',
    '    left: calc(33.3% + 2px);',
    '  }',
    '  :host button[data-testid="form-teaser"] [aria-label="Close modal"] {',
    '    bottom: 6px;',
    '    left: calc(100% - 4px);',
    '    width: 22px;',
    '    height: 22px;',
    '  }',
    '  :host button[data-testid="form-teaser"] h3 {',
    '    gap: 4px;',
    '  }',
    '  :host button[data-testid="form-teaser"] h3::before {',
    '    font-size: 8px;',
    '  }',
    '  :host button[data-testid="form-teaser"] h3::after {',
    '    font-size: 14px;',
    '  }',
    '  :host button[data-testid="form-teaser"] h3[data-pn-whole]::after {',
    '    width: 64px;',
    '    font-size: 10px;',
    '  }',
    '}',
    '/* Slides in from the corner. */',
    '@keyframes pn-ribbon-in {',
    '  from { transform: translate(-100%, 100%); }',
    '  to { transform: none; }',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  :host button[data-testid="form-teaser"] {',
    '    animation: none;',
    '  }',
    '}',
  ].join('\n');

  var CSS_POPUP = [
    '/* The sign-up pop-up. Scoped to the form step, so the success step keeps the',
    "   app's own layout. */",
    ':host #app-embed[data-current-step="form"] [class*="_overlayBackground_"] {',
    '  background-color: rgb(17 17 15 / 0.55) !important;',
    '  opacity: 1 !important;',
    '  -webkit-backdrop-filter: blur(3px);',
    '  backdrop-filter: blur(3px);',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] {',
    '  position: relative;',
    '  width: min(720px, calc(100vw - 32px));',
    '  max-width: none;',
    '  height: auto;',
    '  min-height: 460px;',
    '  grid-template-columns: 1fr 1fr;',
    '  overflow: hidden;',
    '  border-radius: 16px;',
    '  background-color: var(--pn-ivory, #F7F2E8);',
    '  box-shadow: 0 28px 72px rgb(17 17 15 / 0.32);',
    '  animation: pn-popup-in 560ms cubic-bezier(0.22, 1, 0.36, 1) both;',
    '}',
    '/* The photo: the whole dog, as supplied, not a closer crop. The dog sits in',
    '   the right half of the photo, so the crop is anchored right. */',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] > [class*="_gridItem_"]:not([class*="_gridItemContent_"]) {',
    '  position: relative;',
    '  min-height: 100%;',
    '  background-size: cover;',
    '  background-position: 100% 50%;',
    '}',
    '/* The offer seal: a gold foil disc, "10%" over "OFF". */',
    ':host #app-embed[data-current-step="form"] [data-pn-badge]::before {',
    '  content: attr(data-pn-badge-off);',
    '  position: absolute;',
    '  bottom: 22px;',
    '  left: 22px;',
    '  display: flex;',
    '  align-items: flex-end;',
    '  justify-content: center;',
    '  box-sizing: border-box;',
    '  width: 84px;',
    '  height: 84px;',
    '  padding-bottom: 20px;',
    '  border-radius: 50%;',
    '  background: var(--pn-gold-foil, linear-gradient(24deg, #BE9B4E 0%, #D6B25E 55%, #E6CD8C 100%));',
    '  box-shadow: 0 8px 22px rgb(17 17 15 / 0.22), inset 0 0 0 1px rgb(255 255 255 / 0.35);',
    '  color: var(--pn-obsidian, #11110F);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 10px;',
    '  font-weight: 600;',
    '  line-height: 1;',
    '  letter-spacing: 0.2em;',
    '  text-transform: uppercase;',
    '  animation: pn-seal-in 520ms 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both;',
    '}',
    ':host #app-embed[data-current-step="form"] [data-pn-badge]::after {',
    '  content: attr(data-pn-badge);',
    '  position: absolute;',
    '  bottom: 56px;',
    '  left: 22px;',
    '  width: 84px;',
    '  color: var(--pn-obsidian, #11110F);',
    "  font-family: 'Fraunces', Georgia, serif;",
    '  font-size: 26px;',
    '  font-weight: 600;',
    '  line-height: 1;',
    "  font-variation-settings: 'SOFT' 30, 'WONK' 0;",
    '  text-align: center;',
    '  animation: pn-seal-in 520ms 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both;',
    '}',
    '/* The content column. */',
    ':host #app-embed[data-current-step="form"] [class*="_gridItemContent_"] {',
    '  display: flex;',
    '  flex-direction: column;',
    '  justify-content: center;',
    '  box-sizing: border-box;',
    '  padding: 38px 40px 22px;',
    '  text-align: left;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formHeader_"] {',
    '  margin: 0 0 20px;',
    '  padding: 0;',
    '  text-align: left;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formHeader_"]::before {',
    "  content: 'Welcome to PetNexa';",
    '  display: block;',
    '  margin-bottom: 14px;',
    '  color: var(--pn-bark, #4B4038);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 11px;',
    '  font-weight: 600;',
    '  line-height: 1;',
    '  letter-spacing: 0.18em;',
    '  text-transform: uppercase;',
    '}',
    '/* The headline pair: the offer in Fraunces, then an italic line. */',
    ':host #app-embed[data-current-step="form"] h2[data-pn-big] {',
    '  margin: 0 0 14px;',
    '  font-size: 0 !important;',
    '  line-height: 0 !important;',
    '  text-align: left;',
    '}',
    ':host #app-embed[data-current-step="form"] h2[data-pn-big]::before,',
    ':host #app-embed[data-current-step="form"] h2[data-pn-big]::after {',
    '  display: block;',
    "  font-family: 'Fraunces', Georgia, serif;",
    '  font-weight: 600;',
    "  font-variation-settings: 'SOFT' 30, 'WONK' 0;",
    '  letter-spacing: -0.01em;',
    '}',
    ':host #app-embed[data-current-step="form"] h2[data-pn-big]::before {',
    '  content: attr(data-pn-big);',
    '  color: var(--pn-obsidian, #11110F);',
    '  font-size: 42px;',
    '  line-height: 1.04;',
    '  animation: pn-pop 620ms 160ms cubic-bezier(0.34, 1.56, 0.64, 1) both;',
    '}',
    ':host #app-embed[data-current-step="form"] h2[data-pn-big]::after {',
    '  content: attr(data-pn-line);',
    '  color: var(--pn-bark, #4B4038);',
    '  font-size: 34px;',
    '  font-style: italic;',
    '  line-height: 1.12;',
    '  animation: pn-fade-up 560ms 280ms cubic-bezier(0.22, 1, 0.36, 1) both;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_textBody_"] {',
    '  margin: 0;',
    '  padding: 0;',
    '}',
    ':host #app-embed[data-current-step="form"] p[data-pn-text] {',
    '  margin: 0;',
    '  font-size: 0 !important;',
    '  line-height: 0 !important;',
    '}',
    ':host #app-embed[data-current-step="form"] p[data-pn-text]::after {',
    '  content: attr(data-pn-text);',
    '  display: block;',
    '  color: var(--pn-bark, #4B4038);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 15px;',
    '  font-weight: 400;',
    '  line-height: 1.55;',
    '}',
    "/* Fields and the gold button, as the store's own inputs and accent buttons. */",
    '/* Shop’s one-tap sign-up, which Forms adds when email is the only field.',
    '   Its purple button and "or" rule are not in the design; email sign-up is',
    '   unaffected. */',
    ':host #app-embed[data-current-step="form"] [class*="_formFieldset_"] > :has(> shop-lead-capture),',
    ':host #app-embed[data-current-step="form"] shop-lead-capture {',
    '  display: none !important;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formFieldset_"] {',
    '  gap: 12px;',
    '  margin: 0;',
    '  padding: 0;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formInputField_"] {',
    '  box-sizing: border-box;',
    '  height: 52px;',
    '  border: 1px solid var(--pn-rule, #DED5C4);',
    '  border-radius: 12px;',
    '  background-color: var(--pn-white, #FFFFFF);',
    '  box-shadow: none;',
    '  color: var(--pn-obsidian, #11110F);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 15px;',
    '  transition: border-color 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1);',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formInputField_"]:focus {',
    '  border-color: var(--pn-gold-deep, #98783D);',
    '  outline: none;',
    '  box-shadow: 0 0 0 3px var(--pn-gold-024, rgba(214, 178, 94, 0.24));',
    '}',
    '/* The label reads as a placeholder: centred in the field, never floated,',
    '   gone once something is typed. The typed text sits on the same centre line. */',
    ':host #app-embed[data-current-step="form"] [class*="_formInputField_"] {',
    '  padding: 0 16px !important;',
    '  line-height: 50px;',
    '}',
    ':host #app-embed[data-current-step="form"] label[data-pn-label] {',
    '  top: 50% !important;',
    '  left: 17px !important;',
    '  margin: 0 !important;',
    '  color: var(--pn-taupe, #A99783);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 0 !important;',
    '  line-height: 1 !important;',
    '  transform: translateY(-50%) !important;',
    '  transition: opacity 150ms cubic-bezier(0.4, 0, 0.2, 1);',
    '  pointer-events: none;',
    '}',
    ':host #app-embed[data-current-step="form"] label[data-pn-label]::after {',
    '  content: attr(data-pn-label);',
    '  font-size: 15px;',
    '}',
    ':host #app-embed[data-current-step="form"] input:not(:placeholder-shown) + label[data-pn-label] {',
    '  opacity: 0;',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"] {',
    '  box-sizing: border-box;',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  height: 52px;',
    '  margin-top: 2px;',
    '  border: 0;',
    '  border-radius: 12px;',
    '  background-color: var(--pn-gold, #D6B25E);',
    '  box-shadow: none;',
    '  color: var(--pn-obsidian, #11110F);',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '  font-size: 13px;',
    '  font-weight: 600;',
    '  letter-spacing: 0.14em;',
    '  text-transform: uppercase;',
    '  cursor: pointer;',
    '  transition: background-color 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 120ms cubic-bezier(0.4, 0, 0.2, 1);',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"][data-pn-label] {',
    '  font-size: 0;',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"][data-pn-label]::after {',
    '  content: attr(data-pn-label);',
    '  font-size: 13px;',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"]:hover {',
    '  background-color: var(--pn-gold-foil-deep, #BE9B4E);',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"]:active {',
    '  transform: scale(0.98);',
    '}',
    ':host #app-embed[data-current-step="form"] [data-testid="btn-form-submit"]:focus-visible {',
    '  outline: 2px solid var(--pn-obsidian, #11110F);',
    '  outline-offset: 2px;',
    '}',
    '/* The reassurance ticks, "No thanks" and the consent line. */',
    ':host .pn-ticks,',
    ':host .pn-later {',
    '  display: none;',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-ticks {',
    '  display: flex;',
    '  flex-wrap: wrap;',
    '  justify-content: center;',
    '  gap: 6px 16px;',
    '  margin: 16px 0 0;',
    '  padding: 0;',
    '  list-style: none;',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-ticks li {',
    '  display: flex;',
    '  align-items: center;',
    '  gap: 6px;',
    '  color: var(--pn-bark, #4B4038);',
    '  font-size: 12px;',
    '  line-height: 1.3;',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-ticks svg {',
    '  flex: 0 0 auto;',
    '  width: 12px;',
    '  height: 12px;',
    '  color: var(--pn-gold-deep, #98783D);',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-later {',
    '  display: block;',
    '  align-self: center;',
    '  margin: 14px 0 0;',
    '  padding: 4px 8px;',
    '  border: 0;',
    '  background: none;',
    '  color: var(--pn-bark, #4B4038);',
    '  font-size: 13px;',
    '  text-decoration: underline;',
    '  text-decoration-color: var(--pn-taupe, #A99783);',
    '  text-underline-offset: 4px;',
    '  cursor: pointer;',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-later:hover {',
    '  color: var(--pn-obsidian, #11110F);',
    '  text-decoration-color: currentColor;',
    '}',
    ':host #app-embed[data-current-step="form"] .pn-later:focus-visible {',
    '  outline: 2px solid var(--pn-gold-deep, #98783D);',
    '  outline-offset: 2px;',
    '  border-radius: 4px;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formDisclaimer_"] {',
    '  margin: 12px 0 0;',
    '  padding: 0;',
    '  text-align: center;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formDisclaimer_"] p,',
    ':host #app-embed[data-current-step="form"] [class*="_formDisclaimer_"] em {',
    '  margin: 0;',
    '  color: var(--pn-taupe, #A99783);',
    '  font-size: 11px;',
    '  font-style: normal;',
    '  line-height: 1.5;',
    '}',
    "/* The close button: a white disc in the card's top right corner. */",
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] > :has(> [aria-label="Close modal"]) {',
    '  position: absolute;',
    '  top: 14px;',
    '  right: 14px;',
    '  z-index: 3;',
    '  margin: 0;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] [aria-label="Close modal"] {',
    '  display: flex;',
    '  align-items: center;',
    '  justify-content: center;',
    '  width: 36px;',
    '  height: 36px;',
    '  padding: 0;',
    '  border-radius: 50%;',
    '  background-color: var(--pn-white, #FFFFFF);',
    '  box-shadow: 0 2px 10px rgb(17 17 15 / 0.14);',
    '  color: var(--pn-obsidian, #11110F);',
    '  cursor: pointer;',
    '  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1);',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] [aria-label="Close modal"]:hover {',
    '  transform: rotate(90deg);',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] [aria-label="Close modal"] svg {',
    '  width: 12px;',
    '  height: 12px;',
    '}',
    ':host #app-embed[data-current-step="form"] [class*="_formContainer_"] [aria-label="Close modal"] path {',
    '  fill: currentColor;',
    '}',
    '/* Phones: the photo becomes a band over the form. At 4:3 the band shows a',
    '   little over half the photo’s height, the half the whole dog sits in, so',
    '   it fills the band edge to edge without cropping into the face. */',
    '@media screen and (max-width: 640px) {',
    '  :host #app-embed[data-current-step="form"] [class*="_formContainer_"] {',
    '    width: calc(100vw - 24px);',
    '    height: auto !important;',
    '    max-height: calc(100dvh - 24px);',
    '    min-height: 0;',
    '    grid-template-columns: 1fr;',
    '    grid-template-rows: auto auto;',
    '    overflow-y: auto;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [class*="_formContainer_"] > [class*="_gridItem_"]:not([class*="_gridItemContent_"]) {',
    '    display: block !important;',
    '    height: auto;',
    '    min-height: 0;',
    '    aspect-ratio: 4 / 3;',
    '    background-size: 100% auto !important;',
    '    background-position: 50% 66% !important;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [data-pn-badge]::before {',
    '    bottom: 16px;',
    '    left: 16px;',
    '    width: 68px;',
    '    height: 68px;',
    '    padding-bottom: 15px;',
    '    font-size: 9px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [data-pn-badge]::after {',
    '    bottom: 43px;',
    '    left: 16px;',
    '    width: 68px;',
    '    font-size: 21px;',
    '  }',
    '  /* Top aligned, at its own height: centred in a tall row it left a wide',
    '     empty band under the photo. */',
    '  :host #app-embed[data-current-step="form"] [class*="_gridItemContent_"] {',
    '    justify-content: flex-start;',
    '    height: auto !important;',
    '    min-height: 0 !important;',
    '    padding: 24px 22px 20px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [class*="_formHeader_"] {',
    '    margin-bottom: 18px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [class*="_formHeader_"]::before {',
    '    margin-bottom: 10px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] h2[data-pn-big] {',
    '    margin-bottom: 10px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] h2[data-pn-big]::before {',
    '    font-size: 34px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] h2[data-pn-big]::after {',
    '    font-size: 28px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] p[data-pn-text]::after {',
    '    font-size: 14px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] .pn-ticks {',
    '    margin-top: 14px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] .pn-later {',
    '    margin-top: 10px;',
    '  }',
    '  :host #app-embed[data-current-step="form"] [class*="_formContainer_"] > :has(> [aria-label="Close modal"]) {',
    '    top: 12px;',
    '    right: 12px;',
    '  }',
    '}',
    '@keyframes pn-popup-in {',
    '  from { opacity: 0; transform: translateY(18px) scale(0.98); }',
    '  to { opacity: 1; transform: none; }',
    '}',
    '@keyframes pn-pop {',
    '  from { opacity: 0; transform: scale(0.86); }',
    '  to { opacity: 1; transform: none; }',
    '}',
    '@keyframes pn-fade-up {',
    '  from { opacity: 0; transform: translateY(8px); }',
    '  to { opacity: 1; transform: none; }',
    '}',
    '@keyframes pn-seal-in {',
    '  from { opacity: 0; transform: scale(0.6) rotate(-12deg); }',
    '  to { opacity: 1; transform: none; }',
    '}',
    '@media (prefers-reduced-motion: reduce) {',
    '  :host #app-embed[data-current-step="form"] [class*="_formContainer_"],',
    '  :host #app-embed[data-current-step="form"] [data-pn-badge]::before,',
    '  :host #app-embed[data-current-step="form"] [data-pn-badge]::after,',
    '  :host #app-embed[data-current-step="form"] h2[data-pn-big]::before,',
    '  :host #app-embed[data-current-step="form"] h2[data-pn-big]::after {',
    '    animation: none;',
    '  }',
    '}',
  ].join('\n');

  var CSS = [
    ':host, :host * {',
    "  font-family: 'Hanken Grotesk', 'Helvetica Neue', Arial, sans-serif;",
    '}',
    ':host h1, :host h2, :host h3 {',
    "  font-family: 'Fraunces', Georgia, serif;",
    "  font-variation-settings: 'SOFT' 30, 'WONK' 0;",
    '  font-optical-sizing: auto;',
    '}',

    CSS_RIBBON,
    CSS_POPUP,
  ].join('\n');

  var HOSTS = 'shopify-forms-embed, shopify-forms, [id*="shopify-forms"]';
  var flagged = 'pnAppFont';

  function inject(root) {
    if (!root || root[flagged]) return;
    try {
      if (root.adoptedStyleSheets && typeof CSSStyleSheet === 'function' && 'replaceSync' in CSSStyleSheet.prototype) {
        var sheet = new CSSStyleSheet();
        sheet.replaceSync(CSS);
        // Last in the list, so it settles after the app's own stylesheets.
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat([sheet]);
      } else {
        var style = document.createElement('style');
        style.textContent = CSS;
        root.appendChild(style);
      }
      root[flagged] = true;
    } catch (e) {
      /* An app that guards its shadow root is its own business. */
    }
    label(root);
    decorate(root);
    // The teaser and the pop-up mount and unmount inside the shadow root,
    // which the document's observer cannot see.
    if (window.MutationObserver) {
      new MutationObserver(function () {
        label(root);
        decorate(root);
      }).observe(root, { childList: true, subtree: true, characterData: true });
    }
  }

  function setAttr(el, name, value) {
    if (el && el.getAttribute(name) !== value) el.setAttribute(name, value);
  }

  var TICK =
    '<svg viewBox="0 0 12 12" aria-hidden="true" focusable="false"><path d="M2 6.5l2.5 2.5L10 3.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* The pop-up, drawn to the owner's mockup. The offer is read from the
     heading set in the Forms editor ("Get 10% OFF ..."), so the headline, the
     button and the seal follow it if the discount changes. A heading with no
     percentage in it is left as the app draws it. The consent line is the
     app's own, untouched. */
  function decorate(root) {
    var card = root.querySelector('[class*="_formContainer_"]');
    if (!card) return;
    var heading = card.querySelector('[class*="_textHeading_"]');
    var offer = heading && heading.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
    if (!offer) return;
    var pct = offer[1] + '%';

    setAttr(heading, 'data-pn-big', pct + ' off');
    setAttr(heading, 'data-pn-line', 'your first order');
    setAttr(
      card.querySelector('[class*="_textBody_"] p'),
      'data-pn-text',
      'Plus first look at new travel gear and our care guides. Twice a month, never more.'
    );
    setAttr(card.querySelector('[data-testid="btn-form-submit"]'), 'data-pn-label', 'Unlock my ' + pct + ' off');
    var photo = card.querySelector('[class*="_gridItem_"]:not([class*="_gridItemContent_"])');
    setAttr(photo, 'data-pn-badge', pct);
    setAttr(photo, 'data-pn-badge-off', 'off');
    setAttr(card.querySelector('label[for="email"]'), 'data-pn-label', 'Your email address');
    setAttr(card.querySelector('label[for="first_name"]'), 'data-pn-label', 'Your first name');

    var content = card.querySelector('[class*="_gridItemContent_"]');
    var disclaimer = content && content.querySelector('[class*="_formDisclaimer_"]');
    if (!content || content.querySelector('.pn-ticks')) return;

    var ticks = document.createElement('ul');
    ticks.className = 'pn-ticks';
    ticks.innerHTML = ['Free shipping Australia-wide', '14-day returns', 'Unsubscribe any time']
      .map(function (text) {
        return '<li>' + TICK + '<span>' + text + '</span></li>';
      })
      .join('');

    var later = document.createElement('button');
    later.type = 'button';
    later.className = 'pn-later';
    later.textContent = 'No thanks';
    later.addEventListener('click', function () {
      var close = card.querySelector('[aria-label="Close modal"]');
      if (close) close.click();
    });

    content.insertBefore(ticks, disclaimer || null);
    content.insertBefore(later, disclaimer || null);
  }

  /* Splits the teaser text into the ribbon's label and its Fraunces line:
     "10% Off - First Order!" gives "First Order" over "10% off". */
  function label(root) {
    var heading = root.querySelector('button[data-testid="form-teaser"] h3');
    if (!heading) return;
    var text = heading.textContent.replace(/\s+/g, ' ').trim();
    if (heading.getAttribute('data-pn-source') === text) return;
    heading.setAttribute('data-pn-source', text);

    var match = text.match(/(\$?\d+(?:\.\d+)?\s*%?)\s*off\b/i);
    if (match) {
      var rest = text.slice(0, match.index) + ' ' + text.slice(match.index + match[0].length);
      heading.setAttribute('data-pn-big', match[1].replace(/\s+/g, '') + ' off');
      // A bare discount ("10% off") keeps the label it was designed with.
      heading.setAttribute(
        'data-pn-small',
        rest.replace(/[!.,:;|\-–—]+/g, ' ').replace(/\s+/g, ' ').trim() || 'First order'
      );
      heading.removeAttribute('data-pn-whole');
    } else {
      heading.setAttribute('data-pn-big', text);
      heading.setAttribute('data-pn-small', '');
      heading.setAttribute('data-pn-whole', '');
    }
  }

  function scan() {
    var hosts = document.querySelectorAll(HOSTS);
    for (var i = 0; i < hosts.length; i++) {
      if (hosts[i].shadowRoot) inject(hosts[i].shadowRoot);
    }
  }

  scan();

  // The form mounts well after the page does, and again on a later trigger.
  if (window.MutationObserver) {
    new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
